import { API_BASE } from "@/lib/utils";
import { useAuthStore } from "@/features/auth/auth-store";

/**
 * The single chokepoint for every API request made by the app (issue #193).
 *
 * `apiFetch` is the only place in `src/` that is allowed to call the raw
 * `fetch()` API for a backend request — `eslint.config.js` enforces that with
 * a `no-restricted-syntax` rule so a new hand-rolled `fetch()` fails the lint
 * gate instead of silently reintroducing the bug class this module fixes:
 *   - one place to build the URL from `API_BASE`,
 *   - one place to attach auth headers (including the `X-Auth-Token`
 *     second header `rebuy` needs, and the no-auth case `getTables` needs),
 *   - one error shape that surfaces the backend's `detail` field,
 *   - one place that reacts to a 401 (H8): clear the persisted auth store
 *     and send the user to `/login`, so a stale token can't loop them
 *     through every authenticated page re-401ing forever.
 *
 * Callers never build headers or call `fetch` themselves — they pass a path
 * plus `token`/`secondaryToken`/`body`, and get back parsed JSON or a thrown
 * `Error` carrying the backend's `detail` message.
 */

export interface ApiFetchOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  /** JSON-serializable request body. Sets `Content-Type: application/json` when present. */
  body?: unknown;
  /** Bearer token for the `Authorization` header. Omit for unauthenticated calls (e.g. `getTables`). */
  token?: string | null;
  /**
   * Second `X-Auth-Token: Bearer <token>` header. Only `rebuy` needs this:
   * the player token goes in `Authorization` (required by `get_bearer_token`)
   * while the account's auth JWT goes in `X-Auth-Token` for chip deduction.
   */
  secondaryToken?: string | null;
  /**
   * Skip the 401 -> clear-auth-and-redirect handling for this call.
   * Only the pre-auth endpoints (login/register/google-auth) need this: a
   * wrong password legitimately answers 401 and the caller must show that
   * as a form error, not have the chokepoint bounce the user to the page
   * they are already on.
   */
  skipAuthRedirect?: boolean;
}

/** Error thrown by `apiFetch` for any non-2xx response. Always carries the
 * backend's `detail` message when the response body parses as JSON. */
export class ApiRequestError extends Error {
  readonly status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = "ApiRequestError";
    this.status = status;
  }
}

function handleUnauthorized() {
  // Only clear + redirect once the persisted auth store has actually
  // finished hydrating from localStorage. `RequireAuth` never renders
  // anything that issues an authenticated request before a token exists in
  // the store, so this only guards a theoretical early/unauthenticated call
  // racing hydration — but firing `logout()` while zustand's `persist`
  // rehydration is still in flight risks the rehydration finishing right
  // after and writing the stale on-disk token straight back into the store,
  // silently undoing the clear.
  if (!useAuthStore.persist.hasHydrated()) return;

  useAuthStore.getState().logout();

  if (typeof window !== "undefined" && window.location.pathname !== "/login") {
    window.location.assign("/login");
  }
}

/**
 * Issue a single API request. `path` is relative to `API_BASE` (e.g.
 * `/api/me`, `/api/academy/progress`) — callers build that path, this
 * function is the only one that turns it into an absolute URL and the only
 * one that calls `fetch`.
 */
export async function apiFetch<T = unknown>(
  path: string,
  options: ApiFetchOptions = {},
): Promise<T> {
  const { method = "GET", body, token, secondaryToken, skipAuthRedirect = false } = options;

  const headers: Record<string, string> = {};
  if (body !== undefined) headers["Content-Type"] = "application/json";
  if (token) headers.Authorization = `Bearer ${token}`;
  if (secondaryToken) headers["X-Auth-Token"] = `Bearer ${secondaryToken}`;

  const response = await fetch(`${API_BASE}${path}`, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (response.status === 401 && !skipAuthRedirect) {
    handleUnauthorized();
  }

  if (!response.ok) {
    const errorBody = await response
      .json()
      .catch(() => ({ detail: "An unknown error occurred." }));
    throw new ApiRequestError(
      errorBody.detail || `HTTP error! status: ${response.status}`,
      response.status,
    );
  }

  // Empty responses (e.g. a 204 No Content) have no body to parse.
  if (response.status === 204) {
    return { ok: true } as T;
  }

  return response.json();
}
